import { useState, useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import campaignService from '../../../../../services/campaignService';

// Normalize backend group shape to frontend expected shape
const normalizeGroup = (g) => ({
 _id: g._id?.toString?.() || g._id,
 name: g.name,
 status: g.status || 'draft',
 threadId: g.discord?.threadId || null,
 creatorIds: (g.creatorIds || []).map(id => id?.toString?.() || id),
 groupSize: g.maxSize || 10,
 discord: g.discord || {},
 goLiveMessageSentAt: g.goLiveMessageSentAt || null
});

// Normalize backend reaction/creator shape to frontend expected shape
// Use subdocument _id for unique key (supports same creatorId in multiple groups)
const normalizeCreator = (rc) => ({
 _id: rc._id?.toString?.() || (rc.creatorId || rc._id)?.toString?.() || rc.creatorId || rc._id,
 creatorId: (rc.creatorId)?.toString?.() || rc._id?.toString?.(),
 name: rc.name || rc.creator?.name || rc.creator?.twitter?.username || '',
 twitterHandle: rc.twitterHandle || rc.creator?.twitter?.username || '',
 profileImage: rc.creator?.twitter?.profileImageUrl || '',
 status: rc.creator?.status || 'NeedsToSet',
 briefingStatus: rc.briefingStatus || 'REACTED',
 deliveryStatus: rc.deliveryStatus || 'TO_DO',
 groupId: rc.groupId?.toString?.() || null,
 advancedMetrics: rc.creator?.advancedMetrics || {},
 followerCount: rc.creator?.followerAnalysis?.metrics?.followerCount || rc.creator?.twitter?.followers || 0,
 discordId: rc.creator?.discord?.id || '',
 discordUsername: rc.creator?.discord?.username || '',
 lastPaymentDate: rc.lastPaymentDate || null,
 tweet: rc.tweet || null,
 wallet: null
});

const useGroupManagement = (campaignId, campaignData) => {
 const [groups, setGroups] = useState([]);
 const [creators, setCreators] = useState([]);
 const [loading, setLoading] = useState(true);
 const [activatingGroupId, setActivatingGroupId] = useState(null);
 const hasFetched = useRef(false);

 // Load groups from campaignData when it changes
 useEffect(() => {
 if (campaignData?.groups) {
 setGroups(campaignData.groups.map(normalizeGroup));
 }
 }, [campaignData]);

 // Fetch reacted creators from API
 const fetchReactions = useCallback(async () => {
 if (!campaignId) return;
 try {
 const data = await campaignService.getReactions(campaignId);
 if (data?.creators) {
 setCreators(data.creators.map(normalizeCreator));
 }
 // Only re-fetch groups if campaignData had none (auto-migration may have created one)
 // Otherwise trust the groups already loaded from campaignData
 if (!campaignData?.groups?.length) {
 const groupsData = await campaignService.getGroups(campaignId);
 if (groupsData?.groups) {
 setGroups(groupsData.groups.map(normalizeGroup));
 }
 }
 } catch (err) {
 console.error('[useGroupManagement] Failed to fetch reactions:', err);
 }
 }, [campaignId, campaignData]);

 // Initial data load
 useEffect(() => {
 if (!campaignId || hasFetched.current) return;
 hasFetched.current = true;

 const loadData = async () => {
 setLoading(true);
 await fetchReactions();
 setLoading(false);
 };
 loadData();
 }, [campaignId, fetchReactions]);

 // Add a single reacted creator (from WebSocket event)
 const addReactedCreator = useCallback((creatorData) => {
 setCreators(prev => {
 const id = creatorData.creatorId?.toString?.() || creatorData.creatorId;
 const exists = prev.some(c => (c.creatorId || c._id) === id);
 if (exists) return prev;

 return [...prev, {
 _id: id,
 creatorId: id,
 name: creatorData.name || '',
 twitterHandle: creatorData.twitterHandle || '',
 profileImage: creatorData.profileImage || '',
 briefingStatus: 'REACTED',
 deliveryStatus: 'TO_DO',
 groupId: null,
 advancedMetrics: {},
 followerCount: 0,
 discordId: creatorData.discordId || '',
 discordUsername: creatorData.discordUsername || '',
 tweet: null,
 wallet: null
 }];
 });
 }, []);

 // Update creator when tweet submitted via WebSocket
 const handleTweetSubmitted = useCallback((creatorId, tweetData, groupId) => {
 setCreators(prev => prev.map(c =>
 c.creatorId === creatorId && c.groupId === groupId
 ? { ...c, deliveryStatus: 'POSTED', tweet: tweetData }
 : c
 ));
 }, []);

 // Update creator when activity detected via WebSocket
 const handleContentActivity = useCallback((creatorId, groupId) => {
 setCreators(prev => prev.map(c =>
 c.creatorId === creatorId && c.groupId === groupId && c.deliveryStatus === 'TO_DO'
 ? { ...c, deliveryStatus: 'SHOWED_ACTIVITY' }
 : c
 ));
 }, []);

 // Create a new group via API
 const createGroup = useCallback(async ({ name, maxSize } = {}) => {
 const groupName = name || `Group ${groups.length + 1}`;
 const groupSize = maxSize || 10;
 const tempId = `temp-group-${Date.now()}`;

 // Optimistic update
 const optimisticGroup = {
 _id: tempId,
 name: groupName,
 status: 'draft',
 threadId: null,
 creatorIds: [],
 groupSize,
 discord: {}
 };
 setGroups(prev => [...prev, optimisticGroup]);

 try {
 const result = await campaignService.createGroup(campaignId, { name: groupName, maxSize: groupSize });
 const realGroup = normalizeGroup(result.group);

 // Replace temp with real group
 setGroups(prev => prev.map(g => g._id === tempId ? realGroup : g));
 toast.success(`Created ${realGroup.name}`);
 return realGroup._id;
 } catch (err) {
 // Revert optimistic update
 setGroups(prev => prev.filter(g => g._id !== tempId));
 toast.error(`Failed to create group: ${err.response?.data?.error || err.message}`);
 return null;
 }
 }, [campaignId, groups.length]);

 // Unassign creator from a specific group via API
 const unassignCreator = useCallback(async (creatorId, groupId) => {
 if (!groupId) return;

 const entry = creators.find(c =>
 (c.creatorId || c._id) === creatorId && c.groupId === groupId
 );
 if (!entry) return;

 const hasOtherEntries = creators.some(
 c => (c.creatorId || c._id) === creatorId && c.groupId && c.groupId !== groupId
 );

 // Optimistic update
 if (hasOtherEntries) {
 // Remove this entry entirely (creator has other group entries)
 setCreators(prev => prev.filter(c => !(
 (c.creatorId || c._id) === creatorId && c.groupId === groupId
 )));
 } else {
 // Reset to unassigned (only entry for this creator)
 setCreators(prev => prev.map(c =>
 (c.creatorId || c._id) === creatorId && c.groupId === groupId
 ? { ...c, groupId: null, briefingStatus: 'REACTED', deliveryStatus: 'TO_DO', tweet: null }
 : c
 ));
 }
 setGroups(prev => prev.map(g =>
 g._id === groupId
 ? { ...g, creatorIds: g.creatorIds.filter(id => id !== creatorId) }
 : g
 ));

 try {
 await campaignService.removeCreatorFromGroup(campaignId, groupId, creatorId);
 toast.success('Creator unassigned');
 } catch (err) {
 // Revert — re-fetch canonical state
 await fetchReactions();
 toast.error(`Failed to unassign: ${err.response?.data?.error || err.message}`);
 }
 }, [campaignId, creators, fetchReactions]);

 // Bulk assign creators to a group via API
 // creatorIds here are actual creator IDs (not subdocument _ids)
 const bulkAssignCreators = useCallback(async (creatorIds, groupId) => {
 let targetGroupId = groupId;

 // If 'new', create a group first
 if (groupId === 'new') {
 const newGroupId = await createGroup();
 if (!newGroupId) return;
 targetGroupId = newGroupId;
 }

 const group = groups.find(g => g._id === targetGroupId);
 if (!group && groupId !== 'new') {
 toast.error('Group not found');
 return;
 }

 const currentAssigned = creators.filter(c => c.groupId === targetGroupId).length;
 const availableSlots = (group?.groupSize || 10) - currentAssigned;
 const toAssign = creatorIds.slice(0, availableSlots);

 if (toAssign.length === 0) {
 toast.error('Group is full');
 return;
 }

 // Optimistic update — mark unassigned entries for these creators
 setCreators(prev => prev.map(c =>
 toAssign.includes(c.creatorId || c._id) && !c.groupId
 ? { ...c, groupId: targetGroupId, briefingStatus: 'ONBOARDED', deliveryStatus: 'TO_DO' }
 : c
 ));
 setGroups(prev => prev.map(g =>
 g._id === targetGroupId
 ? { ...g, creatorIds: [...g.creatorIds, ...toAssign] }
 : g
 ));

 try {
 const result = await campaignService.assignCreatorsToGroup(campaignId, targetGroupId, toAssign);
 const failedCount = result.results?.failed?.length || 0;
 const successCount = result.results?.success?.length || toAssign.length;

 if (failedCount > 0) {
 toast.success(`Assigned ${successCount}, ${failedCount} failed`);
 } else {
 const remaining = creatorIds.length - toAssign.length;
 if (remaining > 0) {
 toast.success(`Assigned ${successCount}, ${remaining} remaining (group full)`);
 } else {
 toast.success(`Assigned ${successCount} creator${successCount !== 1 ? 's' : ''}`);
 }
 }

 // Refresh to get canonical state (picks up new entries for multi-group creators)
 await fetchReactions();
 } catch (err) {
 // Revert — re-fetch canonical state
 await fetchReactions();
 toast.error(`Failed to assign: ${err.response?.data?.error || err.message}`);
 }
 }, [campaignId, groups, creators, createGroup, fetchReactions]);

 // Manually submit a tweet URL for a creator in a specific group (add or override)
 const submitCreatorPost = useCallback(async (creatorId, tweetUrl, groupId) => {
 const creator = creators.find(c => c._id === creatorId);
 const oldTweet = creator?.tweet;
 const oldStatus = creator?.deliveryStatus;

 // Optimistic update
 setCreators(prev => prev.map(c =>
 c._id === creatorId
 ? { ...c, deliveryStatus: 'POSTED', tweet: { url: tweetUrl } }
 : c
 ));

 try {
 await campaignService.submitCreatorPost(campaignId, creator?.creatorId || creatorId, tweetUrl, groupId);
 toast.success('Content added');
 } catch (err) {
 // Revert
 setCreators(prev => prev.map(c =>
 c._id === creatorId
 ? { ...c, deliveryStatus: oldStatus, tweet: oldTweet }
 : c
 ));
 toast.error(`Failed to add content: ${err.response?.data?.error || err.message}`);
 }
 }, [campaignId, creators]);

 // Activate the group and optionally post its brief to Discord
 const sendGoLiveMessage = useCallback(async (groupId, { briefUrl, message, channelMode } = {}) => {
 setActivatingGroupId(groupId);

 try {
 const result = await campaignService.sendGoLiveMessage(campaignId, groupId, { briefUrl, message, channelMode });
 toast.success(result.goLiveMessageSentAt ? 'Campaign brief posted to Discord!' : 'Discord group is ready');

 if (result.roleSkipped || result.warning) {
 toast(result.warning || 'Discord role limit reached (250). Group activated without a role — clean up old roles in Discord.', {
 icon: '\u26a0\ufe0f',
 duration: 8000,
 style: { background: '#FFF3CD', color: '#856404', border: '1px solid #FFEEBA' }
 });
 }

 // Keep the returned status and Discord destination, including dedicated channels.
 setGroups(prev => prev.map(g =>
 g._id === groupId
 ? { ...g, ...normalizeGroup(result.group) }
 : g
 ));
 return true;
 } catch (err) {
 toast.error(`Failed to go live: ${err.response?.data?.details || err.response?.data?.error || err.message}`);
 return false;
 } finally {
 setActivatingGroupId(null);
 }
 }, [campaignId]);

 // Duplicate an existing group with same creators, fresh statuses
 const duplicateGroup = useCallback(async (groupId) => {
 const sourceGroup = groups.find(g => g._id === groupId);
 if (!sourceGroup) {
 toast.error('Group not found');
 return;
 }

 try {
 const result = await campaignService.duplicateGroup(campaignId, groupId);
 const newGroup = normalizeGroup(result.group);

 // Add the new group
 setGroups(prev => [...prev, newGroup]);

 // Refresh creators to pick up new entries
 await fetchReactions();

 toast.success(`Duplicated as ${newGroup.name}`);
 return newGroup._id;
 } catch (err) {
 toast.error(`Failed to duplicate: ${err.response?.data?.error || err.message}`);
 return null;
 }
 }, [campaignId, groups, fetchReactions]);

 // Manually add a creator by twitter handle
 const addCreator = useCallback(async (twitterUsername) => {
 const cleanHandle = twitterUsername.replace(/^@/, '');
 try {
 const result = await campaignService.addCreatorToCampaign(campaignId, { twitterUsername: cleanHandle });
 toast.success(`Added @${cleanHandle}`);

 // Local state update from response — no page refresh needed
 setCreators(prev => {
 const exists = prev.some(c => (c.creatorId || c._id) === result.creatorId);
 if (exists) return prev;
 return [...prev, {
 _id: result.creatorId,
 creatorId: result.creatorId,
 name: result.name || cleanHandle,
 twitterHandle: result.twitterHandle || cleanHandle,
 profileImage: result.profileImage || '',
 briefingStatus: result.briefingStatus || 'ONBOARDED',
 deliveryStatus: result.deliveryStatus || 'TO_DO',
 groupId: null,
 advancedMetrics: {},
 followerCount: 0,
 discordId: '',
 discordUsername: '',
 tweet: null,
 wallet: null
 }];
 });
 } catch (err) {
 toast.error(err.response?.data?.message || err.message);
 }
 }, [campaignId]);

 // Delete a group (auto-cleans Discord resources on backend for active groups)
 const deleteGroup = useCallback(async (groupId) => {
 const group = groups.find(g => g._id === groupId);
 if (!group) {
 toast.error('Group not found');
 return;
 }

 // Optimistic update — remove group and reset its creators
 const prevGroups = groups;
 setGroups(prev => prev.filter(g => g._id !== groupId));
 setCreators(prev => prev.map(c =>
 c.groupId === groupId
 ? { ...c, groupId: null, briefingStatus: 'REACTED', deliveryStatus: 'TO_DO', tweet: null }
 : c
 ));

 try {
 await campaignService.deleteGroup(campaignId, groupId);
 toast.success(`Deleted ${group.name}`);
 } catch (err) {
 // Revert — re-fetch canonical state
 setGroups(prevGroups);
 await fetchReactions();
 toast.error(`Failed to delete: ${err.response?.data?.error || err.message}`);
 }
 }, [campaignId, groups, fetchReactions]);

 return {
 groups,
 creators,
 loading,
 activatingGroupId,
 setGroups,
 setCreators,
 createGroup,
 unassignCreator,
 bulkAssignCreators,
 submitCreatorPost,
 sendGoLiveMessage,
 duplicateGroup,
 deleteGroup,
 addCreator,
 addReactedCreator,
 fetchReactions,
 handleTweetSubmitted,
 handleContentActivity
 };
};

export default useGroupManagement;
